import { useState } from "react";
import { Button } from "../../components/ui/button";
import { Calendar } from "../../components/ui/calendar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Trash2, Plus, Clock, Calendar as CalendarIcon } from "lucide-react";
import { format, parseISO, isSameDay } from "date-fns";
import { it as itLocale, enUS as enLocale } from "date-fns/locale";
import { useLanguage } from "../../context/LanguageContext";

// Controls that only appear on hover are unreachable on a touch screen, so
// they are always visible below the small breakpoint and hover-revealed above
// it. Deleting a meeting from a phone was impossible before this.
const TOUCH_SAFE_ACTION =
  "opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity";

// Separates a meeting's title from its date in the delete confirmation. A JS
// string, where an escape sequence is decoded; written into JSX text it would
// print as the sequence itself.
const DOT = "\u00b7";

export function MeetingSection({
  meetings, selectedDate, setSelectedDate, meetingDates,
  setMeetingDialogOpen, handleDeleteMeeting, t,
}) {
  const { language } = useLanguage();

  // The meeting pending deletion, or null. Both delete buttons used to fire
  // straight through, and the one in the list below sits beside a row that
  // shows nothing but a title and a time: easy to hit against the wrong one.
  const [pendingDelete, setPendingDelete] = useState(null);

  const getMeetingsForDate = (date) =>
    meetings.filter((m) => {
      try { return isSameDay(parseISO(m.date), date); } catch { return false; }
    });

  const forSelected = getMeetingsForDate(selectedDate);

  // The stored date is YYYY-MM-DD, which is precise and reads like a database
  // field. Shown in the reader's own language instead.
  const meetingDay = (value) => {
    try {
      return format(parseISO(value), "d MMM yyyy", {
        locale: language === "it" ? itLocale : enLocale,
      });
    } catch {
      return value;
    }
  };

  const DeleteButton = ({ meeting, testId }) => (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => setPendingDelete(meeting)}
      data-testid={testId}
      aria-label={t("common.delete")}
      className={`h-8 w-8 shrink-0 text-red-500 hover:text-red-700 ${TOUCH_SAFE_ACTION}`}
    >
      <Trash2 className="w-3.5 h-3.5" />
    </Button>
  );

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-semibold text-slate-900">{t("project.meetings")}</h2>
        <Button size="sm" onClick={() => setMeetingDialogOpen(true)} data-testid="add-meeting-btn" className="bg-indigo-600 hover:bg-indigo-500 text-white shrink-0">
          <Plus className="w-4 h-4 mr-1" />
          {t("project.addMeeting")}
        </Button>
      </div>

      {/* The calendar is capped and centred rather than filling its column.
          On a phone this section spans the whole screen, and a month grid
          stretched to that width turns into an enormous sparse table: the
          same seven columns with several times the row height. Fixed at the
          width it was designed for, it looks the same everywhere. */}
      <div className="calendar-themed rounded-xl p-2 sm:p-3 mb-4 shadow-sm flex justify-center">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={(date) => date && setSelectedDate(date)}
          modifiers={{ hasMeeting: meetingDates }}
          modifiersStyles={{
            hasMeeting: { backgroundColor: '#3b82f6', color: '#ffffff', fontWeight: 600, borderRadius: '6px' }
          }}
          className="rounded-md w-full max-w-[320px]"
        />
      </div>

      {/* Only shown when the chosen day actually has something on it. An empty
          "nothing on this date" line under a calendar is true on most days and
          useful on none, and the full list below already answers it. */}
      {forSelected.length > 0 && (
        <div className="space-y-2 mb-6">
          {forSelected.map((meeting) => (
            <div key={meeting.id} data-testid={`meeting-${meeting.id}`} className="meeting-card rounded-lg p-3 group">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-slate-800 break-words">{meeting.title}</p>
                  <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                    <Clock className="w-3 h-3 shrink-0" />
                    <span>{meeting.start_time} - {meeting.end_time}</span>
                  </div>
                </div>
                <DeleteButton meeting={meeting} testId={`delete-meeting-${meeting.id}`} />
              </div>
            </div>
          ))}
        </div>
      )}

      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wider">{t("project.allMeetings")}</h3>
        <div className="meetings-list rounded-lg divide-y divide-slate-200 overflow-hidden">
          {meetings.length > 0 ? (
            meetings.map((meeting) => {
              const meetingDate = (() => { try { return parseISO(meeting.date); } catch { return null; } })();
              const isPast = meetingDate && meetingDate < new Date(new Date().setHours(0, 0, 0, 0));
              return (
                <div key={meeting.id} data-testid={`all-meeting-${meeting.id}`} className={`p-3 flex items-center gap-2 group ${isPast ? 'opacity-60' : ''}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-800 truncate">{meeting.title}</p>
                    {/* Wraps rather than overflowing: date and time side by
                        side do not fit a narrow column on a phone. */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500 mt-1">
                      <span className="flex items-center gap-1"><CalendarIcon className="w-3 h-3 shrink-0" />{meetingDay(meeting.date)}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3 shrink-0" />{meeting.start_time} - {meeting.end_time}</span>
                    </div>
                  </div>
                  {isPast && <span className="text-xs text-slate-400 shrink-0">{t("project.past")}</span>}
                  <DeleteButton meeting={meeting} testId={`delete-all-meeting-${meeting.id}`} />
                </div>
              );
            })
          ) : (
            <p className="text-center text-sm text-slate-400 py-4">{t("project.noMeetings")}</p>
          )}
        </div>
      </div>

      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => { if (!v) setPendingDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("meeting.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete
                ? `${pendingDelete.title} ${DOT} ${meetingDay(pendingDelete.date)}`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-200">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const id = pendingDelete?.id;
                setPendingDelete(null);
                if (id) handleDeleteMeeting(id);
              }}
              data-testid="confirm-delete-meeting"
              className="bg-red-600 hover:bg-red-700"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
